import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiX, FiExternalLink, FiHeart } from 'react-icons/fi';
import { theme } from '../theme';
import { social } from '../config/social';
import { fetchChannels } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  recordAppOpen, syncEngaged, pickProposal, markEngaged, snooze, recordShown,
} from '../services/support';

// Métadonnées d'affichage par source (URL résolue au runtime : chaînes via /channels, réseaux via env).
const META = {
  yt_saga: { label: 'Saga Infinie', emoji: '⚔️', accent: '#f72585', verb: 'Abonne-toi à', chan: 'saga' },
  yt_vertingo: { label: 'VertinGo', emoji: '🎮', accent: '#7c3aed', verb: 'Abonne-toi à', chan: 'vertingo' },
  yt_horizons: { label: 'Horizons Infinis', emoji: '🔭', accent: '#06b6d4', verb: 'Abonne-toi à', chan: 'horizons' },
  facebook: { label: 'VertinGo sur Facebook', emoji: '👍', accent: '#1877f2', verb: 'Like' },
  tiktok: { label: 'VertinGo sur TikTok', emoji: '🎵', accent: '#ff2d55', verb: 'Suis' },
};

const slideUp = keyframes`from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;
const Wrap = styled.div`
  position: fixed; right: 18px; bottom: 18px; z-index: 3000; width: min(340px, calc(100vw - 36px));
  background: ${theme.colors.bgCard}; border: 1px solid ${theme.colors.borderHover};
  border-radius: ${theme.radii.lg}; box-shadow: 0 16px 48px rgba(0,0,0,.5); overflow: hidden;
  animation: ${slideUp} .35s ease;
`;
const Top = styled.div`height: 5px; background: linear-gradient(90deg, ${({ $a }) => $a}, ${theme.colors.secondary});`;
const Body = styled.div`padding: 1.1rem 1.2rem 1.2rem;
  h4 { font-family: ${theme.fonts.heading}; color: ${theme.colors.textPrimary}; font-size: 1.02rem; margin-bottom: .35rem; }
  p { color: ${theme.colors.textSecondary}; font-size: .86rem; line-height: 1.5; margin-bottom: 1rem; }
  .emo { font-size: 1.15rem; }
`;
const Close = styled.button`position: absolute; top: 8px; right: 8px; background: transparent; border: none;
  color: ${theme.colors.textMuted}; cursor: pointer; font-size: 1rem; &:hover { color: ${theme.colors.textPrimary}; }`;
const Row = styled.div`display: flex; gap: .5rem; flex-wrap: wrap;`;
const Primary = styled.a`display: inline-flex; align-items: center; gap: .45rem; text-decoration: none; cursor: pointer;
  background: ${({ $a }) => $a}; color: #fff; font-weight: 700; font-size: .86rem; padding: .55rem 1rem;
  border-radius: ${theme.radii.full}; &:hover { filter: brightness(1.08); }`;
const Later = styled.button`display: inline-flex; align-items: center; gap: .4rem; cursor: pointer;
  background: transparent; color: ${theme.colors.textMuted}; border: 1px solid ${theme.colors.border};
  font-size: .82rem; padding: .55rem .9rem; border-radius: ${theme.radii.full}; &:hover { color: ${theme.colors.textPrimary}; }`;

// Popup « soutiens VertinGo », ciblée par activité, timing conservateur, mémorisée par source.
export default function SupportPopup() {
  const { isLoggedIn } = useAuth();
  const [shown, setShown] = useState(null); // { id, url }
  const doneThisSession = useRef(false);

  useEffect(() => { recordAppOpen(); }, []);
  useEffect(() => { if (isLoggedIn) syncEngaged(); }, [isLoggedIn]);

  useEffect(() => {
    if (doneThisSession.current) return undefined;
    let alive = true;
    // Laisse l'utilisateur s'installer (~25 s) avant toute sollicitation.
    const t = setTimeout(async () => {
      let channels = [];
      try { channels = await fetchChannels(); } catch { channels = []; }
      const chanUrl = {};
      (Array.isArray(channels) ? channels : []).forEach((c) => { if (c && c.key && c.url) chanUrl[c.key] = c.url; });
      const urlFor = (id) => {
        if (id.startsWith('yt_')) { const u = chanUrl[META[id].chan]; return u ? `${u}${u.includes('?') ? '&' : '?'}sub_confirmation=1` : ''; }
        if (id === 'facebook') return social.facebook;
        if (id === 'tiktok') return social.tiktok;
        return '';
      };
      const available = Object.keys(META).filter((id) => !!urlFor(id));
      const pick = pickProposal(available);
      // IMPRESSION : dès qu'on la montre, on pose le cooldown (backoff) → elle ne revient PAS à chaque session même
      // si l'user ne clique rien.
      if (alive && pick) { doneThisSession.current = true; recordShown(pick); setShown({ id: pick, url: urlFor(pick) }); }
    }, 25000);
    return () => { alive = false; clearTimeout(t); };
  }, []);

  if (!shown) return null;
  const m = META[shown.id];

  // S'abonner : marque la source engagée (ne sera plus jamais proposée) + reset compteur (markEngaged s'en charge).
  const onEngage = () => { markEngaged(shown.id); setTimeout(() => setShown(null), 150); };
  // « Plus tard » : refus explicite → snooze() recule le prochain rappel (backoff).
  const onLater = () => { snooze(); setShown(null); };

  return (
    <Wrap role="dialog" aria-label="Soutiens VertinGo">
      <Top $a={m.accent} />
      <Close onClick={onLater} aria-label="Fermer"><FiX /></Close>
      <Body>
        <h4><FiHeart style={{ color: m.accent, verticalAlign: '-2px' }} /> Tu aimes VertinGo ?</h4>
        <p>
          <span className="emo">{m.emoji}</span> {m.verb} <b>{m.label}</b> — ça nous aide énormément
          à continuer les jeux, les vidéos et les events. Merci ! 🙌
        </p>
        <Row>
          <Primary $a={m.accent} href={shown.url} target="_blank" rel="noreferrer" onClick={onEngage}>
            <FiExternalLink /> {m.verb} {m.label}
          </Primary>
          <Later onClick={onLater}>Plus tard</Later>
        </Row>
      </Body>
    </Wrap>
  );
}
